import { AppConsts } from '@shared/AppConsts';
import { UrlHelper } from '@shared/helpers/UrlHelper';

/**
 * Controls the "Network Unreachable" modal (#apiErrorModal in app.component.html).
 *
 * A failed request with status 0 does not mean the API is down - it only means the browser
 * never received a response. That also happens on a short connectivity blip, on a stale
 * keep-alive socket, and whenever the tab is frozen or the machine sleeps with a request in
 * flight. Showing the modal for every one of those turned any long idle session into a false
 * "Network Unreachable" alarm, so a failure has to be confirmed before anything is displayed.
 *
 * Confirmation is not left to the user clicking something again: each unconfirmed failure
 * schedules a reachability probe, so a single failed action still reaches the threshold within
 * a few seconds if the connection really is broken, and resolves silently if it is not.
 *
 * Once confirmed, the outcome depends on whose fault it is. A genuinely offline browser shows
 * this modal - our API being up or down changes nothing for the user in that state. A client
 * that is online but still can't reach our API is the same outage AppPreBootstrap already sends
 * a fresh page load to /maintenance for, so this redirects there too instead of showing the
 * modal, keeping the experience the same whether the outage is hit on load or mid-session.
 */
export class ApiErrorModalHelper {
    private static readonly selector = '#apiErrorModal';

    /** Consecutive network failures needed before the modal is shown. */
    private static readonly failureThreshold = 3;

    /** Spacing between confirmation probes, and the slower spacing used to detect recovery. */
    private static readonly probeDelayMs = 1500;
    private static readonly recoveryProbeDelayMs = 5000;

    private static visible = false;
    private static listenersAttached = false;
    private static consecutiveFailures = 0;
    private static probeTimer: any = null;

    /**
     * Called for a request that failed with status 0. Ignored while the tab is hidden: the
     * request was almost certainly aborted by the browser freezing the page, so it says nothing
     * about connectivity, and the next real interaction will surface a genuine outage anyway.
     */
    static reportNetworkFailure(): void {
        if (document.hidden) {
            return;
        }

        ApiErrorModalHelper.registerFailure();
    }

    /** Called on every successful response, so the modal clears itself once the API answers again. */
    static reportSuccess(): void {
        ApiErrorModalHelper.consecutiveFailures = 0;
        ApiErrorModalHelper.cancelProbe();

        if (ApiErrorModalHelper.visible) {
            ApiErrorModalHelper.hide();
        }
    }

    static show(): void {
        if (ApiErrorModalHelper.visible) {
            return;
        }

        const $modal = $(ApiErrorModalHelper.selector);
        if (!$modal.length) {
            return;
        }

        ApiErrorModalHelper.attachListeners();
        ApiErrorModalHelper.visible = true;
        $modal.modal('show');
    }

    static hide(): void {
        if (!ApiErrorModalHelper.visible) {
            return;
        }

        ApiErrorModalHelper.visible = false;
        $(ApiErrorModalHelper.selector).modal('hide');
    }

    private static registerFailure(): void {
        ApiErrorModalHelper.consecutiveFailures++;

        // An offline browser is definitive - that one needs no confirmation. It also means the
        // problem is the client's own connection, not our API, so the modal (not a maintenance
        // redirect) is the right message - there's nothing our server being up or down changes.
        if (!navigator.onLine) {
            ApiErrorModalHelper.show();

            if (ApiErrorModalHelper.visible) {
                ApiErrorModalHelper.scheduleProbe(ApiErrorModalHelper.recoveryProbeDelayMs);
            }
            return;
        }

        // Confirmed while still online: the client has a working network path but our own API
        // keeps failing to answer. That's the same condition the initial page load already treats
        // as an outage and sends to /maintenance - reaching that page here too keeps the
        // experience the same regardless of whether the outage was hit on load or mid-session.
        if (ApiErrorModalHelper.consecutiveFailures >= ApiErrorModalHelper.failureThreshold) {
            UrlHelper.redirectToMaintenance();
            return;
        }

        ApiErrorModalHelper.scheduleProbe(ApiErrorModalHelper.probeDelayMs);
    }

    private static scheduleProbe(delayMs: number): void {
        if (ApiErrorModalHelper.probeTimer !== null) {
            return;
        }

        ApiErrorModalHelper.probeTimer = setTimeout(() => {
            ApiErrorModalHelper.probeTimer = null;
            ApiErrorModalHelper.probe();
        }, delayMs);
    }

    private static cancelProbe(): void {
        if (ApiErrorModalHelper.probeTimer !== null) {
            clearTimeout(ApiErrorModalHelper.probeTimer);
            ApiErrorModalHelper.probeTimer = null;
        }
    }

    /**
     * Reachability check against the API. Sent with the same credentials as a normal call so it
     * follows the identical CORS path, and the status is inspected rather than trusted: a
     * gateway in front of a stopped API answers 502/503/504, which is a reachable host but a
     * dead API, so anything from 500 up counts as a failure. A 401 or 404 does prove the API
     * itself answered. The response body is never read.
     */
    private static probe(): void {
        if (document.hidden) {
            // Nothing can be concluded from a frozen tab, and probing here would be throttled.
            ApiErrorModalHelper.consecutiveFailures = 0;
            return;
        }

        if (!navigator.onLine) {
            // Already known to be offline - no point issuing a doomed request.
            ApiErrorModalHelper.registerFailure();
            return;
        }

        const baseUrl = AppConsts.remoteServiceBaseUrl;
        if (!baseUrl) {
            // No endpoint to confirm against - fall back to reporting the original failure.
            ApiErrorModalHelper.show();
            return;
        }

        fetch(`${baseUrl}/api/TokenAuth/CheckSessionStatus`, {
            method: 'GET',
            cache: 'no-store',
            credentials: 'include'
        }).then(
            response => {
                if (response.status >= 500) {
                    ApiErrorModalHelper.registerFailure();
                } else {
                    ApiErrorModalHelper.reportSuccess();
                }
            },
            () => ApiErrorModalHelper.registerFailure()
        );
    }

    private static attachListeners(): void {
        if (ApiErrorModalHelper.listenersAttached) {
            return;
        }

        ApiErrorModalHelper.listenersAttached = true;

        // Cancel button / backdrop dismiss goes through bootstrap, so keep our state in sync and
        // stop probing once the user has dismissed it.
        $(ApiErrorModalHelper.selector).on('hidden.bs.modal', () => {
            ApiErrorModalHelper.visible = false;
            ApiErrorModalHelper.consecutiveFailures = 0;
            ApiErrorModalHelper.cancelProbe();
        });

        // Dismiss as soon as the browser reports connectivity is back.
        window.addEventListener('online', () => ApiErrorModalHelper.reportSuccess());
    }
}
